import { useTranslation } from 'react-i18next';
import { cn, Icon } from '@kognitos/lattice';
import { EOutputCardSize, EOutputCardVariant } from '@/modules/outputs/types';

interface IOutputGlyphProps {
  variant: EOutputCardVariant;
  size: EOutputCardSize;
  loading?: boolean;
}

/*
 * Every output card badges its value with a mark for the value's type. The icon
 * set has nothing for "a parsed document" or "a recorded browser session", so
 * each mark is a handful of spans that the `output-glyph__*` rules in
 * `output-card.css` draw and animate on card hover. One helper per variant,
 * below, keeps the switch at the bottom a lookup rather than a wall of markup.
 */

/**
 * Text: a capital A beside three ruled lines, with a fourth line running the
 * full width beneath — a paragraph of prose reduced to its silhouette.
 */
function TextGlyph() {
  return (
    <span className="output-glyph__text" aria-hidden>
      <span className="output-glyph__text-top">
        <span className="output-glyph__text-letter">A</span>
        <span className="output-glyph__lines">
          <span className="output-glyph__line" />
          <span className="output-glyph__line" />
          <span className="output-glyph__line" />
        </span>
      </span>
      <span className="output-glyph__line" />
    </span>
  );
}

/**
 * File list: two offset pages, the back one peeking out to read as a stack
 * rather than a single document.
 */
function ListGlyph() {
  return (
    <span className="output-glyph__stack" aria-hidden>
      <span className="output-glyph__stack-page" />
      <span className="output-glyph__stack-page" />
    </span>
  );
}

/**
 * Table: five cells laid out by the CSS grid in `output-card.css` — a header
 * row plus a partial body row, so it reads as a grid at 20px.
 */
function TableGlyph() {
  return (
    <span className="output-glyph__table" aria-hidden>
      {Array.from({ length: 5 }, (_, index) => (
        <span key={index} />
      ))}
    </span>
  );
}

/**
 * A single document. The plain variant is a folded page over three text lines;
 * the `idp` variant swaps the fold for extracted-field bars and a scan sweep,
 * distinguishing a parsed document from a raw file.
 */
function PageGlyph({ idp = false }: { idp?: boolean }) {
  return (
    <span className="output-glyph__page" aria-hidden>
      {!idp && <span className="output-glyph__page-fold" />}
      {idp ? (
        <>
          <span className="output-glyph__idp-bar" />
          <span className="output-glyph__page-line" />
          <span className="output-glyph__idp-bar" />
          <span className="output-glyph__idp-scan" />
        </>
      ) : (
        <>
          <span className="output-glyph__page-line" />
          <span className="output-glyph__page-line" />
          <span className="output-glyph__page-line" />
        </>
      )}
    </span>
  );
}

/**
 * Dictionary: literal braces around two short lines, echoing the YAML the
 * card body renders for these values.
 */
function DictGlyph() {
  return (
    <span className="output-glyph__dict" aria-hidden>
      <span>{'{'}</span>
      <span className="output-glyph__dict-lines">
        <span className="output-glyph__dict-line" />
        <span className="output-glyph__dict-line" />
      </span>
      <span>{'}'}</span>
    </span>
  );
}

/** Bespoke CSS illustration used consistently in all output-card sizes. */
export function OutputGlyph({
  variant,
  size,
  loading = false,
}: IOutputGlyphProps) {
  const { t } = useTranslation('outputs');
  let glyph;
  if (loading) {
    glyph = <span className="output-glyph__loader" aria-hidden />;
  } else {
    switch (variant) {
      case EOutputCardVariant.TEXT:
        glyph = <TextGlyph />;
        break;
      case EOutputCardVariant.NUMBER:
        glyph = (
          <span className="output-glyph__mono" aria-hidden>
            {t('outputs.glyph.numberSample')}
          </span>
        );
        break;
      case EOutputCardVariant.LIST:
        glyph = <ListGlyph />;
        break;
      case EOutputCardVariant.TABLE:
        glyph = <TableGlyph />;
        break;
      case EOutputCardVariant.IDP:
        glyph = <PageGlyph idp />;
        break;
      case EOutputCardVariant.FILE:
        glyph = <PageGlyph />;
        break;
      case EOutputCardVariant.CODE:
        glyph = (
          <span className="output-glyph__code" aria-hidden>
            {'</>'}
          </span>
        );
        break;
      case EOutputCardVariant.BROWSER:
        glyph = <Icon type="Globe" size="sm" aria-hidden />;
        break;
      case EOutputCardVariant.DICT:
        glyph = <DictGlyph />;
        break;
      case EOutputCardVariant.EMPTY:
        glyph = <span aria-hidden>—</span>;
        break;
    }
  }

  return (
    // `output-card__glyph` carries no styling of its own any more, but stays as
    // the handle surfaces outside this module use to find the type mark.
    <span
      className={cn(
        'output-card__glyph relative flex flex-none items-center justify-center overflow-hidden rounded-md bg-muted text-muted-foreground',
        // 24px at every card size, so the same value is badged the same way
        // wherever it appears. Only the corner tightens on md.
        'size-6',
        size === EOutputCardSize.MD && 'rounded-[5px]',
      )}
      data-size={size}
      data-testid={`output-glyph-${variant}`}
    >
      {glyph}
    </span>
  );
}
